import { without } from 'lodash-es'

import {
  vDate,
  vMilitaryTime,
  vObject,
  vOptional,
  type vOutput,
  vPicklist,
  vString,
} from '../validation/index.ts'
import type { Workflow } from './common.ts'
import type { LocationType } from './load-location.ts'
import { type ShipType, shipTypes } from './ship-type.ts'

export const flatbedHandlingTypes = [
  'crane',
  'dock-height',
  'rear',
  'rear-side',
  'side',
  'string',
  'string-and-tow',
  'top-side',
] as const

export type FlatbedHandlingType = (typeof flatbedHandlingTypes)[number]

export const flatbedHandlingTypeLabels: Record<FlatbedHandlingType, string> = {
  crane: 'Crane',
  'dock-height': 'Dock height',
  rear: 'Rear',
  'rear-side': 'Rear / side',
  side: 'Side',
  string: 'String',
  'string-and-tow': 'String & Tow',
  'top-side': 'Top / side',
}

export const locationAccessorials = [
  {
    key: 'schedule',
    name: 'Appointment required',
    mode: 'truckload',
    shipTypes: shipTypes,
    locTypes: ['origin', 'stop', 'destination'],
  },
  {
    key: 'driver-assist',
    name: 'Driver assist',
    mode: 'truckload',
    shipTypes: shipTypes,
    locTypes: ['origin', 'stop', 'destination'],
  },
  {
    key: 'notify',
    name: 'Arrival notification',
    mode: 'truckload',
    shipTypes: shipTypes,
    locTypes: ['origin', 'stop', 'destination'],
  },
  {
    key: 'limited-access',
    name: 'Limited access',
    mode: 'ltl',
    shipTypes: ['limited access'],
    locTypes: ['origin', 'destination'],
  },
  {
    key: 'inside',
    name: 'Inside',
    mode: 'ltl',
    shipTypes: without(shipTypes, 'construction', 'business dock'),
    locTypes: ['origin', 'destination'],
  },
  {
    key: 'tradeshow',
    name: 'Tradeshow',
    mode: 'ltl',
    shipTypes: ['trade show'],
    locTypes: ['origin', 'destination'],
  },
  {
    key: 'residential',
    name: 'Residential',
    mode: 'ltl',
    shipTypes: ['residential'],
    locTypes: ['origin', 'destination'],
  },
  {
    key: 'sort-and-segregate',
    name: 'Sort & segregate',
    mode: 'ltl',
    shipTypes: shipTypes,
    locTypes: ['destination'],
  },
  {
    key: 'construction-site',
    name: 'Construction site',
    locTypes: ['origin', 'destination'],
    shipTypes: ['construction'],
    mode: 'ltl',
  },
  {
    key: 'farm',
    name: 'Farm',
    locTypes: ['origin', 'destination'],
    shipTypes: ['farm'],
    mode: 'ltl',
  },
  {
    key: 'military',
    name: 'Government/Military',
    locTypes: ['origin', 'destination'],
    shipTypes: ['military'],
    mode: 'ltl',
  },
  {
    key: 'airport',
    name: 'Airport',
    locTypes: ['origin', 'destination'],
    shipTypes: ['airport'],
    mode: 'ltl',
  },
  {
    key: 'place-of-worship',
    name: 'Place of worship',
    locTypes: ['origin', 'destination'],
    shipTypes: ['place of worship'],
    mode: 'ltl',
  },
  {
    key: 'school',
    name: 'School',
    locTypes: ['origin', 'destination'],
    shipTypes: ['school'],
    mode: 'ltl',
  },
  {
    key: 'mine',
    name: 'Mine',
    locTypes: ['origin', 'destination'],
    shipTypes: ['mine'],
    mode: 'ltl',
  },
  {
    key: 'pier',
    name: 'Pier',
    locTypes: ['origin', 'destination'],
    shipTypes: ['pier'],
    mode: 'ltl',
  },
  {
    key: 'prison',
    name: 'Prison',
    locTypes: ['origin', 'destination'],
    shipTypes: ['prison'],
    mode: 'ltl',
  },
  {
    key: 'liftgate',
    name: 'Liftgate',
    locTypes: ['origin', 'destination'],
    shipTypes: without(shipTypes, 'business dock'),
    mode: 'ltl',
  },
  {
    key: 'signature-required',
    name: 'Signature',
    locTypes: ['destination'],
    shipTypes: shipTypes,
    mode: 'parcel',
  },
  {
    key: 'adult-signature-required',
    name: 'Adult signature',
    locTypes: ['destination'],
    shipTypes: shipTypes,
    mode: 'parcel',
  },
  {
    key: 'indirect-signature-required',
    name: 'Indirect signature',
    locTypes: ['destination'],
    shipTypes: ['residential'],
    mode: 'parcel',
  },
  {
    key: 'home-delivery-premium',
    name: 'Home Delivery Premium',
    locTypes: ['destination'],
    shipTypes: shipTypes,
    mode: 'parcel',
  },
  {
    key: 'twic',
    name: 'TWIC required',
    mode: 'truckload',
    shipTypes: shipTypes,
    locTypes: ['origin', 'stop', 'destination'],
  },
] as const satisfies readonly {
  key: string
  name: string
  locTypes: readonly LocationType[]
  shipTypes: readonly ShipType[]
  mode: Workflow
}[]

export type LocationAccessorialType =
  (typeof locationAccessorials)[number]['key']

export const locationAccessorialSchema = vObject({
  key: vPicklist(locationAccessorials.map(a => a.key)),
  opensAt: vOptional(vMilitaryTime()),
  closesAt: vOptional(vMilitaryTime()),
  tradeshowBooth: vOptional(vString()),
  tradeshowName: vOptional(vString()),
})
export type LocationAccessorialDTO = vOutput<typeof locationAccessorialSchema>

export const loadLocationAccessorialSchema = vObject({
  ...locationAccessorialSchema.entries,
  appointmentDate: vOptional(vDate()),
})
export type LoadLocationAccessorialDTO = vOutput<
  typeof loadLocationAccessorialSchema
>

export const ltlLocationAccessorial = vObject({
  key: vPicklist(
    locationAccessorials
      .filter(a => a.mode === 'ltl')
      .map<LocationAccessorialType>(a => a.key),
  ),
  tradeshowBooth: vOptional(vString()),
})
